import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Flex,
    Group,
    Modal,
    NavLink,
    rem,
    ScrollArea,
    Stack,
    Text,
    TextInput,
    Tooltip,
    UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
    IconChevronDown,
    IconChevronRight,
    IconFileText,
    IconPlus,
    IconSearch,
    IconTrash,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ADMIN_BADGE_COLORS, StringBadges } from "src/components/adminBadges";
import { PromptTemplateCreateModal } from "src/components/promptTemplateCreateModal";
import { PromptTemplateMarkdownPreview } from "src/components/promptTemplateMarkdownPreview";
import type { PromptTemplate } from "src/hooks/useConfiguration";
import { useDeletePromptTemplate } from "src/hooks/usePromptTemplates";
import { useUser } from "src/hooks/useUser";

const GENERAL_PERSONA_GROUP = "General";

function normalizeTemplateTags(tags: PromptTemplate["tags"]): string[] {
    if (!tags?.length) return [];
    return tags.map((tag) => String(tag).trim()).filter(Boolean);
}

function templateMatchesSearch(template: PromptTemplate, query: string): boolean {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return true;

    const terms = trimmed.split(/\s+/).filter(Boolean);
    const title = (template.title ?? "").toLowerCase();
    const persona = (template.persona ?? "").toLowerCase();
    const content = (template.content ?? "").toLowerCase();
    const tags = normalizeTemplateTags(template.tags);
    const tagsJoined = tags.join(" ").toLowerCase();

    return terms.every(
        (term) =>
            title.includes(term) ||
            persona.includes(term) ||
            content.includes(term) ||
            tagsJoined.includes(term) ||
            tags.some((tag) => tag.toLowerCase().includes(term)),
    );
}

type PromptTemplatePickerModalProps = {
    opened: boolean;
    onClose: () => void;
    templates: PromptTemplate[];
    onSelect: (templateId: string) => void;
};

export function PromptTemplatePickerModal({
    opened,
    onClose,
    templates,
    onSelect,
}: PromptTemplatePickerModalProps) {
    const [search, setSearch] = useState("");
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [collapsedPersonas, setCollapsedPersonas] = useState<Set<string>>(new Set());
    const [createModalOpened, setCreateModalOpened] = useState(false);
    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const [templatePendingDelete, setTemplatePendingDelete] = useState<PromptTemplate | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const queryClient = useQueryClient();
    const deletePromptTemplate = useDeletePromptTemplate();
    const { data: user } = useUser();
    const currentUserId = user?.info?.id;

    const isOwnUserTemplate = (template: PromptTemplate) =>
        !!currentUserId && template.created_by_id === currentUserId && !template.is_admin_provided;

    const canDeleteTemplate = (template: PromptTemplate) => isOwnUserTemplate(template);

    const togglePersona = (persona: string) => {
        setCollapsedPersonas((current) => {
            const next = new Set(current);
            if (next.has(persona)) {
                next.delete(persona);
            } else {
                next.add(persona);
            }
            return next;
        });
    };

    const filteredTemplates = useMemo(() => {
        if (!search.trim()) return templates;
        return templates.filter((template) => templateMatchesSearch(template, search));
    }, [templates, search]);

    // Persona is the primary grouping attribute; templates without a persona fall under a
    // "General" group that is always sorted last.
    const groupedTemplates = useMemo(() => {
        const groups = new Map<string, PromptTemplate[]>();
        for (const template of filteredTemplates) {
            const key = template.persona?.trim() || GENERAL_PERSONA_GROUP;
            const existing = groups.get(key);
            if (existing) {
                existing.push(template);
            } else {
                groups.set(key, [template]);
            }
        }
        return Array.from(groups.entries()).sort(([a], [b]) => {
            if (a === GENERAL_PERSONA_GROUP) return 1;
            if (b === GENERAL_PERSONA_GROUP) return -1;
            return a.localeCompare(b);
        });
    }, [filteredTemplates]);

    useEffect(() => {
        if (!opened) {
            setSearch("");
            setCollapsedPersonas(new Set());
            return;
        }
        setSelectedId((current) => {
            if (current && templates.some((template) => template.id === current)) {
                return current;
            }
            return templates[0]?.id ?? null;
        });
    }, [opened, templates]);

    useEffect(() => {
        if (filteredTemplates.length === 0) {
            setSelectedId(null);
            return;
        }
        setSelectedId((current) => {
            if (current && filteredTemplates.some((template) => template.id === current)) {
                return current;
            }
            return filteredTemplates[0].id;
        });
    }, [filteredTemplates]);

    const selectedTemplate = useMemo(
        () => templates.find((template) => template.id === selectedId) ?? null,
        [templates, selectedId],
    );

    const handleUse = () => {
        if (!selectedTemplate) return;
        onSelect(selectedTemplate.id);
        onClose();
    };

    const handleDelete = async () => {
        if (!templatePendingDelete || !canDeleteTemplate(templatePendingDelete)) return;

        setIsDeleting(true);
        try {
            await deletePromptTemplate(templatePendingDelete.id);
            notifications.show({
                title: "Success",
                message: "Prompt template deleted successfully",
                color: "green",
            });
            setDeleteModalOpened(false);
            setTemplatePendingDelete(null);
            if (selectedId === templatePendingDelete.id) {
                setSelectedId(null);
            }
            await queryClient.invalidateQueries({ queryKey: ["prompt-templates"] });
        } catch (error) {
            notifications.show({
                title: "Error",
                message:
                    error instanceof Error ? error.message : "Failed to delete prompt template",
                color: "red",
            });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Prompt Templates"
            size="xl"
            closeButtonProps={{ "aria-label": "Close modal" }}
        >
            <Stack gap="md">
                <Group align="center" gap="sm" wrap="nowrap">
                    <TextInput
                        placeholder="Search by title, persona, tags, or content"
                        size="sm"
                        leftSection={<IconSearch size={16} />}
                        value={search}
                        onChange={(event) => setSearch(event.currentTarget.value)}
                        style={{ flex: 1 }}
                    />
                    <Button
                        size="sm"
                        variant="light"
                        color="purple"
                        leftSection={<IconPlus size={16} />}
                        onClick={() => setCreateModalOpened(true)}
                        style={{ flexShrink: 0 }}
                    >
                        New template
                    </Button>
                </Group>
                <Flex gap="md" align="stretch" style={{ height: rem(440) }}>
                    <Box
                        style={{
                            width: rem(280),
                            flexShrink: 0,
                            border: "1px solid var(--mantine-color-gray-3)",
                            borderRadius: "var(--mantine-radius-sm)",
                            overflow: "hidden",
                        }}
                    >
                        <ScrollArea style={{ height: "100%" }} type="auto">
                            {filteredTemplates.length === 0 ? (
                                <Text size="sm" c="dimmed" p="sm">
                                    No templates found
                                </Text>
                            ) : (
                                groupedTemplates.map(([persona, personaTemplates]) => {
                                    // Only personas with more than one prompt can be collapsed.
                                    const isCollapsible = personaTemplates.length > 1;
                                    const isCollapsed =
                                        isCollapsible && collapsedPersonas.has(persona);
                                    return (
                                        <Box key={persona}>
                                            <UnstyledButton
                                                onClick={
                                                    isCollapsible
                                                        ? () => togglePersona(persona)
                                                        : undefined
                                                }
                                                style={{
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 1,
                                                    display: "block",
                                                    width: "100%",
                                                    background: "var(--mantine-color-body)",
                                                    borderBottom:
                                                        "1px solid var(--mantine-color-gray-2)",
                                                    cursor: isCollapsible ? "pointer" : "default",
                                                }}
                                            >
                                                <Group gap={6} px="sm" py={6} wrap="nowrap">
                                                    {isCollapsible ? (
                                                        isCollapsed ? (
                                                            <IconChevronRight size={14} />
                                                        ) : (
                                                            <IconChevronDown size={14} />
                                                        )
                                                    ) : null}
                                                    <Text
                                                        size="xs"
                                                        fw={700}
                                                        c="dimmed"
                                                        tt="uppercase"
                                                        style={{ flex: 1, minWidth: 0 }}
                                                    >
                                                        {persona}
                                                    </Text>
                                                    {isCollapsible ? (
                                                        <Badge
                                                            size="xs"
                                                            variant="light"
                                                            color="gray"
                                                        >
                                                            {personaTemplates.length}
                                                        </Badge>
                                                    ) : null}
                                                </Group>
                                            </UnstyledButton>
                                            {isCollapsed
                                                ? null
                                                : personaTemplates.map((template) => {
                                                      const isActive = template.id === selectedId;
                                                      const showDelete =
                                                          canDeleteTemplate(template);
                                                      return (
                                                          <Flex
                                                              key={template.id}
                                                              align="stretch"
                                                              gap={0}
                                                              style={{
                                                                  borderBottom:
                                                                      "1px solid var(--mantine-color-gray-1)",
                                                              }}
                                                          >
                                                              <NavLink
                                                                  active={isActive}
                                                                  color="purple"
                                                                  variant="light"
                                                                  onClick={() =>
                                                                      setSelectedId(template.id)
                                                                  }
                                                                  onDoubleClick={() => {
                                                                      onSelect(template.id);
                                                                      onClose();
                                                                  }}
                                                                  leftSection={
                                                                      <IconFileText size={16} />
                                                                  }
                                                                  style={{
                                                                      flex: 1,
                                                                      borderBottom: "none",
                                                                  }}
                                                                  label={
                                                                      <Tooltip
                                                                          label={template.title}
                                                                          multiline
                                                                          withArrow
                                                                          position="top-start"
                                                                          openDelay={300}
                                                                          w={240}
                                                                      >
                                                                          <Text
                                                                              size="sm"
                                                                              fw={
                                                                                  isActive
                                                                                      ? 600
                                                                                      : 500
                                                                              }
                                                                              lineClamp={1}
                                                                          >
                                                                              {template.title}
                                                                          </Text>
                                                                      </Tooltip>
                                                                  }
                                                              />
                                                              {showDelete ? (
                                                                  <Tooltip label="Delete template">
                                                                      <ActionIcon
                                                                          aria-label="Delete template"
                                                                          variant="subtle"
                                                                          color="red"
                                                                          size="lg"
                                                                          onClick={(event) => {
                                                                              event.stopPropagation();
                                                                              setTemplatePendingDelete(
                                                                                  template,
                                                                              );
                                                                              setDeleteModalOpened(
                                                                                  true,
                                                                              );
                                                                          }}
                                                                          style={{
                                                                              flexShrink: 0,
                                                                              alignSelf: "center",
                                                                              marginRight: rem(4),
                                                                          }}
                                                                      >
                                                                          <IconTrash size={16} />
                                                                      </ActionIcon>
                                                                  </Tooltip>
                                                              ) : null}
                                                          </Flex>
                                                      );
                                                  })}
                                        </Box>
                                    );
                                })
                            )}
                        </ScrollArea>
                    </Box>

                    <Box
                        style={{
                            flex: 1,
                            minWidth: 0,
                            border: "1px solid var(--mantine-color-gray-3)",
                            borderRadius: "var(--mantine-radius-sm)",
                            overflow: "hidden",
                        }}
                    >
                        {selectedTemplate ? (
                            <Flex direction="column" style={{ height: "100%" }}>
                                {selectedTemplate.persona?.trim() ? (
                                    <Box
                                        px="sm"
                                        py="xs"
                                        style={{
                                            borderBottom: "1px solid var(--mantine-color-gray-2)",
                                        }}
                                    >
                                        <Text size="sm">Persona: {selectedTemplate.persona}</Text>
                                    </Box>
                                ) : null}
                                <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
                                    <PromptTemplateMarkdownPreview
                                        content={selectedTemplate.content ?? ""}
                                        maxHeight="none"
                                    />
                                </ScrollArea>
                                {normalizeTemplateTags(selectedTemplate.tags).length > 0 && (
                                    <Box
                                        px="sm"
                                        py="xs"
                                        style={{
                                            borderTop: "1px solid var(--mantine-color-gray-2)",
                                        }}
                                    >
                                        <StringBadges
                                            items={normalizeTemplateTags(selectedTemplate.tags)}
                                            color={ADMIN_BADGE_COLORS.tag}
                                        />
                                    </Box>
                                )}
                                {(selectedTemplate.last_modified_time ||
                                    selectedTemplate.is_admin_provided ||
                                    selectedTemplate.created_by_id) && (
                                    <Group
                                        gap="md"
                                        px="sm"
                                        py="xs"
                                        style={{
                                            borderTop: "1px solid var(--mantine-color-gray-2)",
                                        }}
                                    >
                                        {selectedTemplate.last_modified_time && (
                                            <Text size="xs" c="dimmed">
                                                Modified{" "}
                                                {new Date(
                                                    selectedTemplate.last_modified_time,
                                                ).toLocaleString()}
                                            </Text>
                                        )}
                                        {selectedTemplate.is_admin_provided ? (
                                            <Text size="xs" c="dimmed">
                                                Provided by admin
                                            </Text>
                                        ) : (
                                            selectedTemplate.created_by_id && (
                                                <Text size="xs" c="dimmed">
                                                    Created by{" "}
                                                    {isOwnUserTemplate(selectedTemplate)
                                                        ? "you"
                                                        : (selectedTemplate.created_by ??
                                                          selectedTemplate.created_by_id)}
                                                </Text>
                                            )
                                        )}
                                    </Group>
                                )}
                            </Flex>
                        ) : (
                            <Text c="dimmed" size="sm" p="sm">
                                Select a template to preview
                            </Text>
                        )}
                    </Box>
                </Flex>
                <Group justify="flex-end">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button color="green" onClick={handleUse} disabled={!selectedTemplate}>
                        Use template
                    </Button>
                </Group>
            </Stack>
            <Modal
                opened={deleteModalOpened}
                onClose={() => {
                    setDeleteModalOpened(false);
                    setTemplatePendingDelete(null);
                }}
                title={`Delete "${templatePendingDelete?.title ?? ""}"?`}
                size="md"
            >
                <Text mb="md" size="sm">
                    Are you sure you want to delete this prompt template? This action cannot be
                    undone.
                </Text>
                <Group justify="flex-end">
                    <Button variant="outline" onClick={() => setDeleteModalOpened(false)}>
                        Cancel
                    </Button>
                    <Button color="red" loading={isDeleting} onClick={handleDelete}>
                        Delete
                    </Button>
                </Group>
            </Modal>
            <PromptTemplateCreateModal
                opened={createModalOpened}
                onClose={() => setCreateModalOpened(false)}
                onCreated={(id) => {
                    setSearch("");
                    setSelectedId(id);
                }}
            />
        </Modal>
    );
}
